import React from "react";
import { BarChart3, Filter, FileText, Mail, Siren, Users, BoxIcon } from "lucide-react";
import { PixelCursor } from "../alt/PixelCursor";
import { EXPORT_BODY, EXPORT_META, EXPORT_SHADOW, EXPORT_TITLE } from "./exportUi";
/**
 * THE SKILLS LIBRARY, AS A SHELF — AN EXPORT-ONLY ASSET.
 *
 * WHY THIS AND NOT THE SCREEN. `SkillsScreen` is the real product view: four
 * rows in app chrome, sized for someone scanning a list they already
 * understand. As a downloadable asset it under-sells the idea — a list of four
 * rows says "there are four skills", when the claim is that a company builds a
 * LIBRARY of them and every agent draws on it.
 *
 * So this borrows the template wall's treatment, for the same reason it works
 * there: an oversized grid on a ruled board, dissolved into the ground by a
 * single radial mask rather than terminated by a frame. Nothing stops the
 * artwork except light falling off, so the eye assumes the shelf continues
 * past what is shown. One card sits under the light at full weight with the
 * pointer resting on it, which gives a dense grid one thing to actually look
 * at — and names WHICH skill the frame is about.
 *
 * A skill is somebody's expertise written down once, so the author is the
 * credential and the use count is the proof it gets reused. Both are on every
 * card; neither is on the marketing copy.
 *
 * Static by design, like every other frame on the export page.
 */

type Skill = {
  name: string;
  captures: string;
  icon: BoxIcon;
  group: string;
  by: string;
  initials: string;
  color: string;
  used: number;
};

/** Named as the piece of knowledge itself, not as a sentence about it — a
 * library of nouns scans, a list of sentences has to be read. Matches the
 * product screen's own set so the two assets agree. */
const SKILLS: Skill[] = [{
  name: 'Inbound lead qualification',
  captures: 'The questions we ask, the disqualifiers, and where the answer gets written.',
  icon: Filter,
  group: 'Marketing',
  by: 'Priya Nair',
  initials: 'PN',
  color: '#7C2D12',
  used: 142
}, {
  name: 'Renewal email drafting',
  captures: 'Tone, structure and the account facts every renewal note has to carry.',
  icon: Mail,
  group: 'Sales',
  by: 'Marcus Lee',
  initials: 'ML',
  color: '#134E4A',
  used: 211
}, {
  name: 'Weekly reporting format',
  captures: 'Which numbers lead, what gets compared, and how the summary is written.',
  icon: BarChart3,
  group: 'Leadership',
  by: 'Sarah Chen',
  initials: 'SC',
  color: '#1E3A5F',
  used: 96
}, {
  name: 'P1 incident escalation',
  captures: 'Who is paged, in what order, and what the first customer update says.',
  icon: Siren,
  group: 'Customer Success',
  by: 'Tom Alvarez',
  initials: 'TA',
  color: '#3F3A2E',
  used: 38
},
// The shelf shows one more card than fits so the row runs off the right edge.
// These two exist to be cropped — the library has to look longer than the
// frame for the fade to mean anything.
{
  name: 'Contract redline review',
  captures: 'The clauses we never accept, and the fallback language for each one.',
  icon: FileText,
  group: 'Legal',
  by: 'Dana Okafor',
  initials: 'DO',
  color: '#4C1D95',
  used: 64
}, {
  name: 'Candidate debrief notes',
  captures: 'What each interviewer covers, and how a recommendation is written up.',
  icon: Users,
  group: 'People',
  by: 'Jules Weber',
  initials: 'JW',
  color: '#164E3B',
  used: 51
}];

/**
 * THE SHELF RUNS OFF BOTH EDGES.
 *
 * The first version dimmed the un-lit cards to 72% and dropped a radial
 * vignette over everything, which made the library look tentative — three
 * greyed options and one selected, as if you were being asked to choose. The
 * claim is the opposite: a company accumulates dozens of these and every one
 * is live.
 *
 * So nothing is dimmed. Every card is at full strength, the row is WIDER than
 * the frame, and a horizontal fade cuts it at both sides — the same device a
 * marquee uses to say "this continues". What you see is a window onto a longer
 * shelf, not the whole of a small one.
 */
const EDGE_FADE = 'linear-gradient(to right, transparent 0%, #000 11%, #000 89%, transparent 100%)';
/** A touch of softening at the very bottom only, so the ruled board dissolves
 * into the plate instead of ending on a hard line. */
const BASE_FADE = 'linear-gradient(to bottom, #000 0%, #000 82%, transparent 100%)';
function SkillCard({
  skill,
  className = ''
}: {
  skill: Skill;
  className?: string;
}) {
  const Icon = skill.icon;
  return <div className={`rounded-[16px] border border-[#EFE7DA] bg-white p-4 ${className}`} data-id="element-3314" data-mp="rwn8lo86ephj">
      <div className="flex items-center gap-2.5" data-id="element-3315" data-mp="dx5gozzl2w3y">
        {/* The ink tile matches the product screen's skill rows, so the asset
         and the real UI read as the same application. */}
        <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-[#2B211A] shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" data-id="element-3316" data-mp="2yn3813shzl7b">
          <Icon size={14} strokeWidth={1.9} className="text-white" data-id="element-3317" data-mp="2v4nk7x9yop7z" />
        </span>
        <span className="truncate text-[12px] font-semibold text-[#8A7B6D]" data-id="element-3318" data-mp="2q5uguxoe2elj">{skill.group}</span>
      </div>

      <p className={`${EXPORT_TITLE} mt-3`} data-id="element-3319" data-mp="352tjs88apj9o">{skill.name}</p>
      <p className={`${EXPORT_BODY} mt-1.5`} data-id="element-3320" data-mp="352brfmor7p71">{skill.captures}</p>

      {/* Authorship and reuse, on one line. The person is the credential; the
       count is the evidence it was worth writing down. */}
      <div className="mt-4 flex items-center gap-2 border-t border-[#F1EAE0] pt-3" data-id="element-3321" data-mp="2cepbrg294u3t">
        <span aria-hidden="true" className="flex size-[18px] shrink-0 items-center justify-center rounded-full text-[7.5px] font-bold text-white" style={{
        backgroundColor: skill.color
      }} data-id="element-3322" data-mp="27gw74rglk85n">
          {skill.initials}
        </span>
        <span className={`${EXPORT_META} truncate font-semibold text-[#2B211A]`} data-id="element-3323" data-mp="1tny3yixwxbrj">{skill.by}</span>
        <span className="ml-auto shrink-0 rounded-full bg-[#F1EAE0] px-2 py-0.5 text-[11.5px] font-bold text-[#6B5D52]" data-id="element-3324" data-mp="1oov031to5bl5">
          {skill.used} uses
        </span>
      </div>
    </div>;
}
export function SkillShelf({
  /**
   * How many cards share the shelf. Four on a wide artboard; one on a phone
   * artboard, where the same four cards at a quarter of 400px would be
   * illegible. The cards keep their own size in both — the shelf re-composes
   * rather than scaling, which is the difference between an asset that reads
   * on a vertical video and one that has been shrunk to fit.
   */
  columns = 4
}: {
  columns?: 1 | 2 | 4;
} = {}) {
  // A single column is a phone artboard: one card, whole, nothing cropped —
  // cutting the only card in the frame would read as a mistake rather than as
  // a shelf continuing. Wider artboards get the overflowing row.
  const cropped = columns > 1;
  // One more card than fits, so the row is guaranteed to run past the right
  // edge and the fade has something real to cut through.
  const visible = cropped ? SKILLS.slice(0, columns + 1) : SKILLS.slice(0, 1);
  return <div aria-hidden="true" className="relative w-full select-none overflow-hidden" style={{
    maskImage: cropped ? EDGE_FADE : BASE_FADE,
    WebkitMaskImage: cropped ? EDGE_FADE : BASE_FADE
  }} data-id="element-3325" data-mp="1968xuwdjxt6e">
      {/* The board the library is ruled on. Grid only, no fill and no border,
       so the fade is the only thing that defines where it ends. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.55]" style={{
      backgroundImage: 'linear-gradient(to right, #E7DCCB 1px, transparent 1px), linear-gradient(to bottom, #E7DCCB 1px, transparent 1px)',
      backgroundSize: '72px 72px'
    }} data-id="element-3326" data-mp="ugjl03w3k0qa" />

      {/* Cards sized against the FRAME, then pulled left so the row starts
       mid-card. Percentage widths keep the type at a constant size whatever
       the frame is scaled to. */}
      <div className={`relative flex gap-5 py-10 ${cropped ? '-ml-[9%]' : 'px-6'}`} data-id="element-3327" data-mp="2atrx4q7l6wxt">
        {visible.map((skill, index) => <div key={skill.name} className="relative flex-none" style={cropped ? {
        width: `calc((100% - ${(columns - 1) * 20}px) / ${columns})`
      } : undefined} data-id="element-3328" data-mp="25wrp49r6fk42" data-mpi={index}>
            {/* Every card at full strength. The one difference is depth: the
             second card lifts a little, which is where the pointer is. */}
            <SkillCard skill={skill} className={index === 1 ? EXPORT_SHADOW : 'shadow-[0_1px_2px_rgba(43,33,26,0.05),0_12px_28px_-18px_rgba(43,33,26,0.18)]'} data-id="element-3329" data-mp="19zw89v6dzuvr" />
            {/* Half on and half off the card, so it reads as resting over the
             library rather than as an icon inside one. */}
            {index === (cropped ? 1 : 0) && <PixelCursor className="pointer-events-none absolute -bottom-6 -left-5 h-14 w-auto drop-shadow-[0_8px_14px_rgba(43,33,26,0.35)]" data-id="element-3330" data-mp="n085lsthdfmp" />}
          </div>)}
      </div>
    </div>;
}